import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleRegular, WarningRegular } from '@fluentui/react-icons';
import { useMyMoments } from '../../services/complianceApi';
import { firstFilingMoments, monthName, periodCompleteMoment } from './periodMoment';
import { useMoment } from './useMoment';

// Spec § 3.2 (the progress ring) and § 3.3 (the zero-overdue state) — always-visible, personal,
// never comparative — and the trigger for § 3.1, the hooray, which fires exactly when the ring
// closes because both read the same server facts. Renders nothing when the server cannot say who
// you are (`available: false`).

const RING = 56;
const STROKE = 6;
const R = (RING - STROKE) / 2;
const C = 2 * Math.PI * R;

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  ringTrack: { stroke: tokens.colorNeutralStroke2 },
  ringFill: { stroke: tokens.colorBrandForeground1, transitionProperty: 'stroke-dashoffset', transitionDuration: '0.4s' },
  ringDone: { stroke: tokens.colorPaletteGreenForeground1 },
  numbers: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  title: { fontWeight: tokens.fontWeightSemibold },
  status: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  calm: { color: tokens.colorPaletteGreenForeground1 },
  warn: { color: tokens.colorPaletteDarkOrangeForeground1 },
});

export default function MyPeriodCard() {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { data } = useMyMoments();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const facts = data?.available ? data.period : undefined;

  // The month's hooray first, then any first filings (§ 3.5), quiet; the host shows them in turn.
  const moments = useMemo(() => {
    if (!facts) return null;
    const hooray = periodCompleteMoment(facts, t, locale, data?.onTimeStreak ?? 0);
    return [...(hooray ? [hooray] : []), ...firstFilingMoments(data?.firstFilings, t)];
  }, [facts, t, locale, data?.onTimeStreak, data?.firstFilings]);
  useMoment(moments);

  if (!facts) return null;

  const month = monthName(facts.period, locale);
  const fraction = facts.owed > 0 ? facts.filed / facts.owed : 0;
  const overdue = data?.overdue ?? 0;

  return (
    <section className={styles.card} data-testid="my-period-card" aria-label={t('myPeriod.title', { month })}>
      <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} role="img"
        aria-label={t('myPeriod.ringLabel', { filed: facts.filed, owed: facts.owed, month })}>
        <circle className={styles.ringTrack} cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} />
        <circle
          className={facts.complete ? styles.ringDone : styles.ringFill}
          data-testid="my-period-ring"
          data-fraction={fraction.toFixed(3)}
          cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - fraction)}
          transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
        />
      </svg>
      <div className={styles.numbers}>
        <Text className={styles.title}>{t('myPeriod.title', { month })}</Text>
        <Text>
          {facts.owed > 0
            ? t('myPeriod.filedOf', { filed: facts.filed, owed: facts.owed })
            : t('myPeriod.nothingOwed', { month })}
        </Text>
        {(data?.onTimeStreak ?? 0) >= 2 && (
          <Text data-testid="my-streak">{t('myPeriod.streak', { count: data?.onTimeStreak })}</Text>
        )}
      </div>
      {overdue === 0 ? (
        <span className={`${styles.status} ${styles.calm}`} data-testid="my-overdue-calm">
          <CheckmarkCircleRegular aria-hidden="true" />
          <Text>{t('myPeriod.nothingOverdue')}</Text>
        </span>
      ) : (
        <span className={`${styles.status} ${styles.warn}`} data-testid="my-overdue-count">
          <WarningRegular aria-hidden="true" />
          <Text>{t('myPeriod.overdue', { count: overdue })}</Text>
        </span>
      )}
    </section>
  );
}
